import { TextField } from "@mui/material";

/**
 * CoAppTextField — outlined text field used across modals (Create Rule wizard,
 * user creation, group creation, etc.). Brand-teal focus ring, soft border,
 * compact-but-comfortable padding. Pass any extra props through to MUI's
 * <TextField>; consumers can still override `variant` for a one-off if needed.
 *
 * QA: the previous focused state used a 1.5px teal border that read as
 * chunky/disjointed against the floating label — especially in empty fields
 * where the label sits in the notch. Replaced with a clean 1px border + a
 * soft 3px alpha-teal halo (box-shadow) so focus is clearly indicated
 * without competing with the label text for legibility. Label color stays
 * teal on focus, but its weight is dropped slightly to feel less heavy.
 */
export default function CoAppTextField(props) {
	return (
		<TextField
			variant="outlined"
			fullWidth
			size="small"
			margin="dense"
			{...props}
			sx={{
				"& .MuiOutlinedInput-root": {
					// Use the 8px input radius from the design system. NOTE: the
					// sx `borderRadius` shorthand multiplies theme.shape.borderRadius
					// (8px here), so the old `2` rendered as 16px. Use an explicit
					// pixel value so the field matches the rest of the input system.
					borderRadius: "8px",
					background: "#FFFFFF",
					fontSize: 14,
					fontWeight: 500,
					transition: "border-color 0.18s, box-shadow 0.18s",
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "rgba(13, 31, 48, 0.14)",
						borderWidth: 1,
						transition: "border-color 0.18s"
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "rgba(2, 213, 171, 0.5)"
					},
					// Focused state: keep the border at 1px (no chunky 1.5px
					// jump) and rely on a soft alpha-teal halo for focus
					// affordance. Reads as "this field is active" without the
					// label looking like it's fighting the border.
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#02D5AB",
						borderWidth: 1
					},
					"&.Mui-focused": {
						boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)"
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#E03030"
					},
					"&.Mui-error.Mui-focused": {
						boxShadow: "0 0 0 3px rgba(224, 48, 48, 0.18)"
					},
					"&.Mui-disabled": {
						background: "rgba(13, 31, 48, 0.03)"
					}
				},
				"& .MuiInputLabel-root": {
					fontSize: 13,
					fontWeight: 500,
					color: "rgba(13, 31, 48, 0.55)",
					"&.Mui-focused": {
						color: "#057A6D",
						fontWeight: 500
					}
				},
				"& .MuiOutlinedInput-input": {
					padding: "10px 12px"
				},
				"& .MuiFormHelperText-root": {
					marginLeft: 0,
					marginTop: 0.5,
					fontSize: 11,
					fontWeight: 500,
					letterSpacing: "0.01em",
					color: "rgba(13, 31, 48, 0.45)",
					"&.Mui-error": {
						color: "#E03030",
					},
				},
				...((props && props.sx) || {})
			}}
		/>
	);
}
